/**
 * ErrorBoundary — Catches React rendering errors and shows a friendly fallback.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      // Error UI uses translations via a small wrapper
      const ErrorFallback = () => {
        const { t } = require('../../i18n/LanguageContext').useLanguage();
        const title = t('errorBoundary.title');
        const description = t('errorBoundary.description');
        const reloadPage = t('errorBoundary.reloadPage');
        const goHome = t('errorBoundary.goHome');
        return (
        <div className="min-h-screen flex items-center justify-center bg-surface-50 p-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 rounded-full bg-danger-50 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={32} className="text-danger-500" />
            </div>
            <h1 className="page-title mb-2">{title}</h1>
            <p className="text-sm text-text-muted mb-6">
              {description}
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => window.location.reload()} className="btn btn-primary">
                <RefreshCw size={16} /> {reloadPage}
              </button>
              <a href="/login" className="btn btn-outline">
                <Home size={16} /> {goHome}
              </a>
            </div>
          </div>
        </div>
      );
      };
      return <ErrorFallback />;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
